import React, { useState, useMemo } from 'react';
import Button from '@material-ui/core/Button';
import Space from '../../../theme/Space';
import GivelifyModal from '../../../theme/GivelifyModal';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { AppState } from '../../../store';
import { SignupState } from '../../../store/signup/types';
import { object, string } from 'yup';
import { signSetAction } from '../../../store/signup/actions';
import { useTranslation } from 'react-i18next';
import { emailRegExp, phoneRegExp } from '../utils/validationRegExp';
import { TextInput } from '../../../theme/TextInput';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from '@material-ui/core/styles';
import createStyles from '@material-ui/core/styles/createStyles';
import Label16 from '../../../theme/labels/Label16';
import editIconUrl from '../../../assets/edit-icon.svg';
import { yupResolver } from '@hookform/resolvers/yup';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        editButton: {
            cursor: 'pointer',
        },
        editTextStyles: {
            display: 'inline-block',
            marginLeft: theme.spacing(2),
        },
    }),
);

export const EditButton: React.FC<{ handleOpen: () => unknown }> = ({
    handleOpen,
}) => {
    const { t } = useTranslation();
    const editText = t('labels.edit');
    const { editButton, editTextStyles } = useStyles();
    return (
        <div className={editButton} onClick={handleOpen}>
            <img src={editIconUrl} alt={editText} title={editText} />
            <Label16 isPrimary className={editTextStyles}>
                {editText}
            </Label16>
        </div>
    );
};

const EditUserInfoModal: React.FC = () => {
    const [open, setOpen] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const { signUp } = useSelector((state: AppState) => ({
        signUp: state.Signup,
    }));
    const handleOpen = () => {
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
    };

    const { t } = useTranslation();
    const copy = useMemo(
        () => ({
            continue: t('labels.continue'),
            editInfo: t('signup.reviewStep.editInfo'),
            userTitle: t('signup.reviewStep.textFields.userTitle'),
            userFirstName: t('signup.reviewStep.textFields.userFirstName'),
            userLastName: t('signup.reviewStep.textFields.userLastName'),
            userEmail: t('signup.reviewStep.textFields.userEmail'),
            userPhone: t('signup.reviewStep.textFields.userPhone'),
            userModalText: t('signup.reviewStep.userModalText'),

            required: t('error.required'),
            emailRequired: t('error.signup.email'),
            phoneRequired: t('error.signup.phone'),
        }),
        [t],
    );

    const form = useForm<Partial<SignupState>>({
        mode: 'onBlur',
        resolver: yupResolver(
            object<Partial<SignupState>>({
                userTitle: string().required(copy.required).max(25),
                userFirstName: string().required(copy.required).max(55),
                userLastName: string().required(copy.required).max(55),
                userEmail: string()
                    .matches(emailRegExp, copy.emailRequired)
                    .max(255),
                userPhone: string()
                    .matches(phoneRegExp, copy.phoneRequired)
                    .max(25),
            }),
        ),
    });

    const handleSubmit = async () => {
        const values = form.getValues();
        dispatch(
            signSetAction({
                userTitle: values.userTitle,
                userFirstName: values.userFirstName,
                userLastName: values.userLastName,
                userEmail: values.userEmail,
                userPhone: values.userPhone,
            }),
        );
        handleClose();
    };

    return (
        <>
            <EditButton handleOpen={handleOpen}>{copy.editInfo}</EditButton>
            <GivelifyModal
                label={copy.userModalText}
                isOpen={open}
                handleClose={handleClose}
            >
                <form onSubmit={form.handleSubmit(handleSubmit)}>
                    <TextInput<Partial<SignupState>>
                        type="text"
                        name="userTitle"
                        label={copy.userTitle}
                        placeholder={copy.userTitle}
                        formRef={form}
                        defaultValue={signUp.userTitle}
                    />
                    <TextInput<Partial<SignupState>>
                        type="text"
                        name="userFirstName"
                        label={copy.userFirstName}
                        placeholder={copy.userFirstName}
                        formRef={form}
                        defaultValue={signUp.userFirstName}
                    />
                    <TextInput<Partial<SignupState>>
                        type="text"
                        name="userLastName"
                        label={copy.userLastName}
                        placeholder={copy.userLastName}
                        formRef={form}
                        defaultValue={signUp.userLastName}
                    />
                    <TextInput<Partial<SignupState>>
                        type="text"
                        name="userEmail"
                        label={copy.userEmail}
                        placeholder={copy.userEmail}
                        formRef={form}
                        defaultValue={signUp.userEmail}
                    />
                    <TextInput<Partial<SignupState>>
                        type="phone"
                        name="userPhone"
                        label={copy.userPhone}
                        placeholder={copy.userPhone}
                        formRef={form}
                        defaultValue={signUp.userPhone}
                    />
                    <Space gap={3} />
                    <Button type="submit" variant="contained">
                        {copy.continue}
                    </Button>
                </form>
            </GivelifyModal>
        </>
    );
};

export default EditUserInfoModal;
